import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Briefcase, MapPin, Clock, ChevronDown, ArrowRight, Sparkles } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import { useNavigate } from 'react-router-dom';

const CareersPage: React.FC = () => {
  const navigate = useNavigate();
  const { localize } = useTranslation();
  const settings = useAdminStore((s) => s.settings);
  const careers = settings?.careersContent;
  const language = localize({ en: 'en', ka: 'ka' });
  const galleryName = localize(settings?.galleryName) || 'Gallery Fotoatelier';

  const pageTitle = localize(careers?.pageTitle) || (language === 'ka' ? 'კარიერა' : 'Careers');
  const subtitle = localize(careers?.subtitle) || (language === 'ka'
    ? 'შემოუერთდი ჩვენს გუნდს და გახდი თანამედროვე ფოტოხელოვნების სამყაროს ნაწილი.'
    : 'Join our team and become part of the contemporary fine art photography world.');
  const noJobsText = localize(careers?.noJobsText) || (language === 'ka'
    ? 'ამჟამად ვაკანსიები არ არის. თუმცა, ყოველთვის ვეძებთ ნიჭიერ ადამიანებს — გამოგვიგზავნეთ CV.'
    : 'No open positions at the moment. However, we are always looking for talented people — send us your CV.');

  const positions = careers?.positions ?? [];

  const [expandedId, setExpandedId] = React.useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white dark:bg-black transition-colors duration-700">
      {/* Hero */}
      <section className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6 md:px-24">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-2xl sm:text-4xl md:text-5xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-tight">
              {pageTitle}
            </h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mt-6 text-black/40 dark:text-white/40 text-base sm:text-lg font-light max-w-xl leading-relaxed"
            >
              {subtitle}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Positions */}
      <section className="px-4 sm:px-6 md:px-24 pb-20 sm:pb-32">
        <div className="max-w-4xl mx-auto">
          {positions.length > 0 ? (
            <div className="space-y-4">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-[9px] font-bold tracking-[0.4em] text-black/20 dark:text-white/20 uppercase mb-8"
              >
                {positions.length} {language === 'ka' ? 'ღია პოზიცია' : 'Open Position'}{positions.length !== 1 ? (language === 'ka' ? '' : 's') : ''}
              </motion.p>

              {positions.map((pos: { title: { en: string; ka: string }; type: { en: string; ka: string }; location: { en: string; ka: string }; description: { en: string; ka: string }; requirements: { en: string; ka: string } }, idx: number) => {
                const isExpanded = expandedId === idx;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.08, duration: 0.5 }}
                    className="border border-black/5 dark:border-white/5 rounded-2xl overflow-hidden hover:border-black/10 dark:hover:border-white/10 transition-all bg-white dark:bg-black"
                  >
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : idx)}
                      className="w-full flex items-center gap-4 p-5 sm:p-6 text-left hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors"
                    >
                      <div className="w-12 h-12 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center shrink-0">
                        <Briefcase size={20} className="text-black/40 dark:text-white/40" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-base font-bold uppercase tracking-wider text-black dark:text-white">
                          {localize(pos.title)}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 mt-1.5">
                          {localize(pos.type) && (
                            <span className="flex items-center gap-1 text-[10px] text-black/40 dark:text-white/40">
                              <Clock size={11} /> {localize(pos.type)}
                            </span>
                          )}
                          {localize(pos.location) && (
                            <span className="flex items-center gap-1 text-[10px] text-black/40 dark:text-white/40">
                              <MapPin size={11} /> {localize(pos.location)}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronDown size={18} className={`text-black/20 dark:text-white/20 transition-transform duration-300 shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 sm:px-6 pb-6 pt-1 border-t border-black/5 dark:border-white/5 space-y-5">
                            {localize(pos.description) && (
                              <div>
                                <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-2">
                                  {language === 'ka' ? 'აღწერა' : 'Description'}
                                </p>
                                <p className="text-sm text-black/60 dark:text-white/60 font-light leading-relaxed whitespace-pre-line">
                                  {localize(pos.description)}
                                </p>
                              </div>
                            )}
                            {localize(pos.requirements) && (
                              <div>
                                <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 mb-2">
                                  {language === 'ka' ? 'მოთხოვნები' : 'Requirements'}
                                </p>
                                <p className="text-sm text-black/60 dark:text-white/60 font-light leading-relaxed whitespace-pre-line">
                                  {localize(pos.requirements)}
                                </p>
                              </div>
                            )}
                            <button
                              onClick={() => navigate('/contact')}
                              className="flex items-center gap-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity"
                            >
                              {language === 'ka' ? 'გამოაგზავნე CV' : 'Apply Now'} <ArrowRight size={14} />
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="text-center py-16"
            >
              <div className="w-20 h-20 rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] flex items-center justify-center mx-auto mb-6">
                <Sparkles size={32} className="text-black/10 dark:text-white/10" />
              </div>
              <p className="text-sm text-black/40 dark:text-white/40 font-light max-w-md mx-auto leading-relaxed">
                {noJobsText}
              </p>
              <button
                onClick={() => navigate('/contact')}
                className="mt-6 flex items-center gap-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity mx-auto"
              >
                {language === 'ka' ? 'დაგვიკავშირდით' : 'Contact Us'} <ArrowRight size={14} />
              </button>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
